import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  CleaningServices as CleaningServicesIcon,
  Storage as StorageIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import apiService from '../services/api';
import EmailTestsSection from '../components/EmailTestsSection';
import SiteMaintenanceSettingsSection from '../components/SiteMaintenanceSettingsSection';

type Preview = Awaited<ReturnType<typeof apiService.getMaintenancePreview>>;

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<'images' | 'orphans' | null>(null);
  const [lastImagesResult, setLastImagesResult] = useState<{ deleted_files: number; skipped: string[] } | null>(null);
  const [lastOrphansResult, setLastOrphansResult] = useState<{
    deleted_messages: number;
    deleted_conversations: number;
    purged_announcements: number;
    removed_found_objects: number;
  } | null>(null);
  const [confirmImagesOpen, setConfirmImagesOpen] = useState(false);
  const [confirmOrphansOpen, setConfirmOrphansOpen] = useState(false);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const data = await apiService.getMaintenancePreview();
      setPreview(data);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Impossible de charger l’aperçu';
      setPreviewError(msg);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const runUnreferencedImages = async () => {
    setConfirmImagesOpen(false);
    setActionLoading('images');
    setLastImagesResult(null);
    try {
      const r = await apiService.postMaintenanceUnreferencedImages();
      setLastImagesResult(r);
      await loadPreview();
    } catch (e: unknown) {
      setPreviewError(e instanceof Error ? e.message : 'Échec du nettoyage des fichiers');
    } finally {
      setActionLoading(null);
    }
  };

  const runOrphanRecords = async () => {
    setConfirmOrphansOpen(false);
    setActionLoading('orphans');
    setLastOrphansResult(null);
    try {
      const r = await apiService.postMaintenanceOrphanRecords();
      setLastOrphansResult(r);
      await loadPreview();
    } catch (e: unknown) {
      setPreviewError(e instanceof Error ? e.message : 'Échec du nettoyage en base');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorageIcon color="primary" />
        Paramètres
      </Typography>

      <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} sx={{ mb: 3 }}>
        <Tab label="Site public" />
        <Tab label="Nettoyage" />
        <Tab label="Test emails" />
      </Tabs>

      {activeTab === 0 ? (
        <SiteMaintenanceSettingsSection />
      ) : activeTab === 2 ? (
        <EmailTestsSection />
      ) : (
        <>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        Nettoyage
      </Typography>

      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Sauvegarde obligatoire avant toute opération destructive
        </Typography>
        <Typography variant="body2">
          Sauvegardez la base de données (dump SQL) et le répertoire <code>public/uploads</code> (au minimum{' '}
          <code>public/uploads/announcements</code>) avant de lancer un nettoyage. Les actions ci-dessous sont{' '}
          <strong>irréversibles</strong>.
        </Typography>
      </Alert>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" color="primary" onClick={loadPreview} disabled={previewLoading}>
          {previewLoading ? <CircularProgress size={22} color="inherit" /> : 'Prévisualiser'}
        </Button>
        {preview && (
          <Typography variant="body2" color="text.secondary">
            {preview.backup_notice}
          </Typography>
        )}
      </Box>

      {previewError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPreviewError(null)}>
          {previewError}
        </Alert>
      )}

      {preview && (
        <>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CleaningServicesIcon fontSize="small" />
                Fichiers images sans référence
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fichiers présents dans <code>public/uploads/announcements</code> qui ne sont référencés par aucun champ{' '}
                <code>images</code> d’une annonce ni d’un objet trouvé (même logique d’extraction que le reste de
                l’admin).
              </Typography>
              <Chip label={`${preview.unreferenced_announcement_image_files.count} fichier(s)`} color="primary" variant="outlined" sx={{ mr: 1 }} />
              {preview.unreferenced_announcement_image_files.examples.length > 0 && (
                <>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Exemples (max. 20) :
                  </Typography>
                  <List dense disablePadding sx={{ maxHeight: 160, overflow: 'auto' }}>
                    {preview.unreferenced_announcement_image_files.examples.map((name) => (
                      <ListItem key={name} disablePadding>
                        <ListItemText primaryTypographyProps={{ variant: 'body2', fontFamily: 'monospace' }} primary={name} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="warning"
                disabled={actionLoading !== null || preview.unreferenced_announcement_image_files.count === 0}
                onClick={() => setConfirmImagesOpen(true)}
              >
                Supprimer les fichiers orphelins
              </Button>
            </CardActions>
          </Card>

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Lignes orphelines en base de données
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Messages et conversations dont l’annonce n’existe plus ; annonces sans utilisateur valide ou avec
                partenaire référencé mais supprimé ; objets trouvés sans partenaire valide. Les annonces et objets
                concernés sont purgés comme lors d’une suppression admin (matchings, favoris, messages, conversations,
                images).
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip label={`Messages orphelins : ${preview.orphan_messages}`} variant="outlined" />
                <Chip label={`Conversations orphelines : ${preview.orphan_conversations}`} variant="outlined" />
                <Chip label={`Annonces orphelines : ${preview.orphan_announcements}`} variant="outlined" />
                <Chip label={`Objets trouvés orphelins : ${preview.orphan_found_objects}`} variant="outlined" />
              </Box>
            </CardContent>
            <CardActions>
              <Button
                variant="outlined"
                color="error"
                disabled={
                  actionLoading !== null ||
                  (preview.orphan_messages === 0 &&
                    preview.orphan_conversations === 0 &&
                    preview.orphan_announcements === 0 &&
                    preview.orphan_found_objects === 0)
                }
                onClick={() => setConfirmOrphansOpen(true)}
              >
                Nettoyer la base (orphelins)
              </Button>
            </CardActions>
          </Card>
        </>
      )}

      {lastImagesResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Fichiers supprimés : {lastImagesResult.deleted_files}
          {lastImagesResult.skipped.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              Non supprimés ({lastImagesResult.skipped.length}) : {lastImagesResult.skipped.join(', ')}
            </>
          )}
        </Alert>
      )}

      {lastOrphansResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Messages supprimés : {lastOrphansResult.deleted_messages} ; conversations : {lastOrphansResult.deleted_conversations} ; annonces purgées :{' '}
          {lastOrphansResult.purged_announcements} ; objets trouvés retirés : {lastOrphansResult.removed_found_objects}
        </Alert>
      )}

      <Dialog open={confirmImagesOpen} onClose={() => setConfirmImagesOpen(false)}>
        <DialogTitle>Confirmer la suppression des fichiers</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vous allez supprimer définitivement {preview?.unreferenced_announcement_image_files.count ?? 0} fichier(s)
            sous <code>public/uploads/announcements</code>. Avez-vous effectué une sauvegarde ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmImagesOpen(false)}>Annuler</Button>
          <Button onClick={runUnreferencedImages} color="warning" variant="contained" autoFocus>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOrphansOpen} onClose={() => setConfirmOrphansOpen(false)}>
        <DialogTitle>Confirmer le nettoyage en base</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cette opération supprime les lignes orphelines et purge les annonces / objets trouvés incohérents. Une
            sauvegarde SQL récente est indispensable. Continuer ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOrphansOpen(false)}>Annuler</Button>
          <Button onClick={runOrphanRecords} color="error" variant="contained" autoFocus>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default SettingsPage;
